import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, Button } from 'antd';
import { useStores } from '../../store';
import { observer } from 'mobx-react-lite';
import AppsIcon from '@svg/apps-logo.svg?react';
import './apps-recommendation-modal.scss';
import { EventType } from '@src/dtos/analytics.dto';
import { APPS_RECOMMENDATION_TOOLTIP_SHOWED, APPS_RECOMMENDED_NOTIFICATION_TEXT, PENDING_DISABLE_REASON } from '@src/utils/constants';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import managedAppsService from '../../../services/managed-apps.service';
import { useNavigate } from 'src/utils/use-navigate';
import { TooltipDisableReason } from '@src/components/shared/tooltip-disable-message/tooltip-disable-reason';

interface AppsRecommendationModalProps {
    onClose: () => void;
}

const AppsRecommendationModal: React.FC<AppsRecommendationModalProps> = observer(({ onClose }) => {
    const { fwVersionStore, notificationStore, userStore, customerStore } = useStores();
    const [selectedInboundApps, setSelectedInboundApps] = useState<string[]>([]);
    const [selectedOutboundApps, setSelectedOutboundApps] = useState<string[]>([]);
    const [isUpdated, setIsUpdated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const navigate = useNavigate();

    useEffect(() => {
        setSelectedInboundApps(fwVersionStore.recommendedAppData?.inboundRecommendedApps ?? []);
        setSelectedOutboundApps(fwVersionStore.recommendedAppData?.outboundRecommendedApps ?? []);
    }, [fwVersionStore.recommendedAppData]);

    const handleInboundChange = (app: string, checked: boolean) => {
        setSelectedInboundApps(prevState =>
            checked ? [...prevState, app] : prevState.filter(a => a !== app)
        );
    };

    const handleOutboundChange = (app: string, checked: boolean) => {
        setSelectedOutboundApps(prevState =>
            checked ? [...prevState, app] : prevState.filter(a => a !== app)
        );
    };

    const handleClose = () => {
        if (!isUpdated) {
            fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'cancel', location: 'policyUpdateRecommendation' });
        }
        if (!localStorage.getItem(APPS_RECOMMENDATION_TOOLTIP_SHOWED)) {
            notificationStore.setNotificationPopoverText(APPS_RECOMMENDED_NOTIFICATION_TEXT);
            notificationStore.setIsNotificationTooltipShow(true);
            localStorage.setItem(APPS_RECOMMENDATION_TOOLTIP_SHOWED, 'true');
        }
        if (isUpdated) {
            void fwVersionStore.loadVersionsList().then(() =>
                navigate(`/${customerStore.customerId}/${customerStore.serviceId}/protection/version/${fwVersionStore.draftVersion?.externalId}`,
                    { replace: true })
            );
        }
        setIsUpdated(false);
        onClose();
    };

    const updatePolicy = () => {
        const hasDraft = !!fwVersionStore.draftVersion;
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'updateDraft', location: 'policyUpdateRecommendation' });
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: managedAppsService.addRecommendedApps(
                customerStore.customerId,
                customerStore.serviceId,
                selectedOutboundApps,
                selectedInboundApps
            ),
            successCallback: () => {
                setIsUpdated(true);
                setIsLoading(false);
                if (!hasDraft) {
                    fwVersionStore.loadVersionsList().then(() => {
                        const externalId = fwVersionStore.draftVersion?.externalId;
                        if (externalId) {
                            const versionPath = `version/${externalId}/${fwVersionStore.versionProtectionPath}`;
                            navigate(`/${customerStore.customerId}/${customerStore.serviceId}/protection/${versionPath}`);
                        }
                    }).catch(() => { /* navigation skipped on load failure */ });
                }
            },
            errorCallback: () => {
                setIsLoading(false);
            },
            notifObj: { show: false }
        });
    };

    const isDisabled = !!fwVersionStore.pendingVersion;
    const disableReason = isDisabled ? PENDING_DISABLE_REASON : undefined;

    return (
        <Modal open={true} onCancel={handleClose} className="recommendation-modal"
            width={690} closable={true} centered footer={null}>
            <div className="recommendation-modal-content">
                {isUpdated ? <>
                    <div className="recommended-app-title">Policy recommendations are in your draft policy</div>
                    <div className="recommended-app-subtitle">
                        For the updates to take effect, please go to your draft and click <span>Apply Policy</span>.
                    </div>
                    <div className="recommendation-footer">
                        <div className="recommendation-footer-btns-wrapper">
                            <Button className="custom-btn primary-btn large-btn" onClick={handleClose}>Close</Button>
                        </div>
                    </div>
                </> :
                    <>
                        <div className="recommended-app-title">Policy Recommendations</div>
                        <div className="recommended-app-subtitle">
                            After analyzing your network traffic,
                            we have new recommendations for policy V{fwVersionStore.recommendedAppData?.versionNumber} to
                            enhance your business protection:
                        </div>
                        <div className="recommended-app-wrapper">
                            <div className="recommended-app-list-title">
                                <div className="icon-apps-wrapper"><AppsIcon /></div>
                                <div className="recommended-app-list-text">Apps</div>
                            </div>
                            <div className="recommended-app-list">
                                <div className="recommended-app-items">
                                    <div className="recommended-list-title">
                                        Allow {fwVersionStore.recommendedAppData?.inboundRecommendedApps.length} inbound
                                    </div>
                                    <div className="recommended-list">
                                        {fwVersionStore.recommendedAppData?.inboundRecommendedApps.map(app => (
                                            <Checkbox
                                                key={app}
                                                checked={selectedInboundApps.includes(app) && !isDisabled}
                                                onChange={(e) => handleInboundChange(app, e.target.checked)}
                                                disabled={isDisabled}
                                            >
                                                {app}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </div>
                                <div className="recommended-app-items">
                                    <div className="recommended-list-title">
                                        Allow {fwVersionStore.recommendedAppData?.outboundRecommendedApps.length} outbound
                                    </div>
                                    <div className="recommended-list">
                                        {fwVersionStore.recommendedAppData?.outboundRecommendedApps.map(app => (
                                            <Checkbox
                                                key={app}
                                                checked={selectedOutboundApps.includes(app) && !isDisabled}
                                                onChange={(e) => handleOutboundChange(app, e.target.checked)}
                                                disabled={isDisabled}
                                            >
                                                {app}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!userStore.isReadOnlyUser && <div className="recommendation-footer">
                            <div className="recommendation-footer-msg">Updates will be saved in your draft policy.</div>
                            <div className="recommendation-footer-btns-wrapper">
                                <Button className="custom-btn secondary-btn large-btn" onClick={handleClose}>Cancel</Button>
                                <TooltipDisableReason overlayClassName="disable-tooltip" disableReason={disableReason}>
                                    <Button className="custom-btn primary-btn large-btn"
                                            disabled={(!selectedOutboundApps.length && !selectedInboundApps.length) || isLoading || isDisabled}
                                            onClick={updatePolicy}>
                                            {fwVersionStore.draftVersion ? 'Update' : 'Create'} Draft
                                    </Button>
                                </TooltipDisableReason>
                            </div>
                        </div>}
                    </>}
            </div>
            {promiseContextHolder}
        </Modal>
    );
});

export default AppsRecommendationModal;
